<template>
  <div
    v-if="!isClosed"
    class="ui-alert"
    :class="[
      `ui-alert--${tone}`,
      { 'ui-alert--extended': isExtended, 'ui-alert--action-right': actionPlacement === 'right' },
    ]"
    :role="isUrgent ? 'alert' : 'status'"
  >
    <component
      :is="icon"
      class="ui-alert__icon"
      :size="20"
      :stroke-width="1.8"
      aria-hidden="true"
    />

    <div class="ui-alert__body">
      <p v-if="title" class="ui-alert__title">
        <span v-if="typeLabelKey" class="visually-hidden">{{ $t(typeLabelKey) }}: </span>{{ title }}
      </p>

      <div class="ui-alert__text">
        <span v-if="typeLabelKey && !title" class="visually-hidden">{{ $t(typeLabelKey) }}: </span>

        <slot />
      </div>

      <div v-if="$slots.action && actionPlacement === 'below'" class="ui-alert__action">
        <slot name="action" />
      </div>
    </div>

    <div v-if="$slots.action && actionPlacement === 'right'" class="ui-alert__action">
      <slot name="action" />
    </div>

    <button
      v-if="dismissible && tone !== 'danger'"
      type="button"
      class="ui-alert__close"
      :aria-label="$t('Close alert')"
      @click="onClose"
    >
      <X :size="16" :stroke-width="2" aria-hidden="true" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useSlots } from "vue";
import { CircleCheck, CircleX, Info, TriangleAlert, X } from "lucide-vue-next";

const props = withDefaults(
  defineProps<{
    tone?: "info" | "success" | "warning" | "danger";
    title?: string;
    dismissible?: boolean;
    actionPlacement?: "below" | "right";
  }>(),
  {
    tone: "info",
    title: undefined,
    dismissible: false,
    actionPlacement: "below",
  },
);

const emit = defineEmits<{
  close: [];
}>();

const slots = useSlots();

const isClosed = ref(false);

const icons = { info: Info, success: CircleCheck, warning: TriangleAlert, danger: CircleX };

const icon = computed(() => icons[props.tone]);

const isUrgent = computed(() => props.tone === "warning" || props.tone === "danger");

const isExtended = computed(() => Boolean(props.title) || Boolean(slots.action));

const typeLabelKey = computed(() => {
  const keys = { info: "Information", success: "", warning: "Warning", danger: "Error" };

  return keys[props.tone];
});

const onClose = (): void => {
  isClosed.value = true;

  emit("close");
};
</script>
